import type { Schema } from '@cli-types/core-engine/schema.js'
import { toCamelCase, toPascalCase } from '@cli-utils/text.js'
import { flattenFields } from '@core-engine/schema/field-helpers/flattening/flatten-fields.js'
import { isLayoutField } from '@core-engine/schema/field-type-guards/is-layout-field.js'
import { isUserRelationshipTarget } from '@core-engine/schema/system-relationships/is-user-relationship-target.js'
import { toDrizzleType } from '@core-engine/schema/type-mappers/drizzle/to-drizzle-type.js'
import { renderTableDefinitionGeneratedValueTemplate } from '@next-adapter/templates/renderers/generators/database/generate-table-definition.js'
import { getFieldModifiers } from './get-field-modifiers.js'

export function generateTableDefinition(
  schema: Schema,
  requiredImports: Set<string>,
  needsSql: { value: boolean }
): string {
  const tableName = toPascalCase(schema.name)
  const variableName = toCamelCase(schema.name)
  const isSingle = schema.type === 'single'

  requiredImports.add('pgTable')

  const dbFields = flattenFields(schema.fields).filter(
    (field) =>
      !(field.type === 'relationship' && field.multiple === true) && !isLayoutField(field.type)
  )

  const hasDraftMode = schema.actions?.draft === true
  const hasPublishedField = dbFields.some((field) => field.name === 'published')

  const fieldDefs = dbFields
    .map((field) => {
      const drizzleType = toDrizzleType(field, requiredImports)
      const modifiers = getFieldModifiers(field, needsSql)
      const reference =
        field.type === 'relationship' && isUserRelationshipTarget(field.relationship)
          ? ".references(() => user.id, { onDelete: 'set null' })"
          : ''
      const line = `    ${field.name}: ${drizzleType}${reference}${modifiers}`

      if ((field.type === 'richtext' || field.type === 'markdown') && field.output === 'html') {
        const snakeName = field.name.replace(/([A-Z])/g, '_$1').toLowerCase()
        requiredImports.add('text')
        return `${line},\n    ${field.name}Html: text('${snakeName}_html')`
      }

      return line
    })
    .join(',\n')

  let publishedField = ''
  if (hasDraftMode && !hasPublishedField) {
    requiredImports.add('boolean')
    publishedField = `,\n    published: boolean().notNull().default(false)`
  }

  const hasCreatedAt = dbFields.some((field) => field.name === 'createdAt')
  const hasUpdatedAt = dbFields.some((field) => field.name === 'updatedAt')
  const hasCreatedBy = dbFields.some((field) => field.name === 'createdBy')
  const hasUpdatedBy = dbFields.some((field) => field.name === 'updatedBy')
  let generatedFields = ''
  if (!hasCreatedAt || !hasUpdatedAt) {
    requiredImports.add('timestamp')
    needsSql.value = true
  }
  if (!hasCreatedAt) {
    generatedFields += `,\n    createdAt: timestamp({ precision: 3, mode: 'string' }).default(sql\`CURRENT_TIMESTAMP\`).notNull()`
  }
  if (!hasUpdatedAt) {
    generatedFields += `,\n    updatedAt: timestamp({ precision: 3, mode: 'string' }).default(sql\`CURRENT_TIMESTAMP\`).notNull()`
  }
  if (!hasCreatedBy || !hasUpdatedBy) {
    requiredImports.add('text')
  }
  if (!hasCreatedBy) {
    generatedFields += `,\n    createdBy: text().references(() => user.id, { onDelete: 'set null' })`
  }
  if (!hasUpdatedBy) {
    generatedFields += `,\n    updatedBy: text().references(() => user.id, { onDelete: 'set null' })`
  }

  const hasSortOrder = dbFields.some((field) => field.name === 'sortOrder')
  let sortOrderField = ''
  if (!isSingle && !hasSortOrder) {
    requiredImports.add('integer')
    sortOrderField = `,\n    sortOrder: integer().notNull().default(0)`
  }
  const hasSlugField = !isSingle && dbFields.some((field) => field.name === 'slug')
  const indexes = hasSlugField
    ? `,\n  (table) => [\n    uniqueIndex('${tableName}_slug_key').on(table.slug)\n  ]`
    : ''
  if (hasSlugField) {
    requiredImports.add('uniqueIndex')
  }

  return renderTableDefinitionGeneratedValueTemplate({
    variableName: variableName,
    tableName: tableName,
    fieldDefs: fieldDefs,
    publishedField: publishedField,
    generatedFields: generatedFields,
    sortOrderField: sortOrderField,
    indexes: indexes
  })
}
